import React, { useEffect, useState } from 'react';
import shuffle from 'lodash.shuffle';
import { useTranslation } from 'react-i18next';

const TrueOrFalse = ({ events, onQuit }) => {
  const { t } = useTranslation();

  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [result, setResult] = useState(null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [gameOver, setGameOver] = useState(false);

  useEffect(() => {
    if (!events.length) return;
    const prepared = shuffle(events.filter(e => e.title && e.year)).slice(0, 10).map(event => {
      const isTrue = Math.random() > 0.5;
      let fakeYear = event.year;

      if (!isTrue) {
        fakeYear = parseInt(event.year) + (Math.random() > 0.5 ? 10 : -10);
      }

      return {
        title: event.title,
        realYear: event.year,
        displayedYear: fakeYear,
        isTrue,
      };
    });
    setQuestions(prepared);
  }, [events]);
  const handleChoice = (choice) => {
    if (selectedAnswer !== null) return;
    const current = questions[currentIndex];
    const correct = current.isTrue ? 'true' : 'false';

    setSelectedAnswer(choice);
    if (choice === correct) {
      setResult(true);
      setScore(prev => prev + 1);
      setStreak(prev => {
        const newStreak = prev + 1;
        if (newStreak > bestStreak) setBestStreak(newStreak);
        return newStreak;
      });
    } else {
      setResult(false);
      setStreak(0);
    }
  };

  const handleNext = () => {
    if (currentIndex + 1 < questions.length) {
      setCurrentIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setResult(null);
    } else {
      setGameOver(true);
    }
  };

  const handleQuit = () => {
    if (window.confirm(t('quiz.trueOrFalse.quitConfirm'))) {
      onQuit();
    }
  };

  if (questions.length === 0) {
    return <div className="text-center text-gray-400 mt-10">{t('quiz.trueOrFalse.loading')}</div>;
  }

  if (gameOver) {
    return (
      <div className="max-w-xl mx-auto p-6 bg-gray-900 text-white rounded-lg shadow-xl mt-10 text-center">
        <h2 className="text-3xl font-bold text-purple-400 mb-4">{t('quiz.trueOrFalse.gameOver')}</h2>
        <p className="text-lg text-green-400 mb-2">
          {t('quiz.trueOrFalse.finalScore')}: {score} / {questions.length}
        </p>
        <p className="text-md text-yellow-400 mb-6">
          🏆 {t('quiz.trueOrFalse.bestStreak')}: {bestStreak}
        </p>
        <button
          onClick={onQuit}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold"
        >
          🔙 {t('quiz.trueOrFalse.returnToMenu')}
        </button>
      </div>
    );
  }

  const current = questions[currentIndex];
  return (
    <div className="relative max-w-2xl mx-auto p-6 bg-gray-900 text-white rounded-lg shadow-xl mt-10">
      <button
        onClick={handleQuit}
        className="absolute top-4 right-4 text-red-400 hover:text-red-500 text-sm font-bold"
      >
        ✖️ {t('quiz.trueOrFalse.quit')}
      </button>

      <div className="flex justify-center mb-6">
        <h2 className="text-2xl font-bold text-purple-400">
          ✅❌ {t('quiz.mode.trueOrFalse')} ({currentIndex + 1}/{questions.length})
        </h2>
      </div>

      <div className="bg-gray-800 p-6 rounded-lg mb-6 text-center">
        <h3 className="text-xl font-semibold text-blue-300 mb-2">
          "{current.title}" {t('quiz.trueOrFalse.occurredIn')} {current.displayedYear}.
        </h3>
      </div>

      <div className="flex flex-col md:flex-row gap-6 justify-center">
        {['true', 'false'].map((choice) => (
          <button
            key={choice}
            onClick={() => handleChoice(choice)}
            disabled={selectedAnswer !== null}
            className={`flex-1 p-4 rounded-lg font-semibold transition text-xl ${
              selectedAnswer !== null
                ? choice === (current.isTrue ? 'true' : 'false')
                  ? 'bg-green-600 text-white'
                  : choice === selectedAnswer
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-800 text-white'
                : 'bg-gray-800 hover:bg-purple-700 text-white'
            }`}
          >
            {choice === 'true' ? t('quiz.options.true') : t('quiz.options.false')}
          </button>
        ))}
      </div>

      {selectedAnswer !== null && (
        <div className="mt-6 text-center space-y-4">
          {result ? (
            <p className="text-green-400 font-bold text-lg">
              ✅ {t('quiz.trueOrFalse.correct')} {current.realYear}.
            </p>
          ) : (
            <p className="text-red-400 font-bold text-lg">
              ❌ {t('quiz.trueOrFalse.incorrect')} {current.realYear}.
            </p>
          )}
          <button
            onClick={handleNext}
            className="bg-green-600 hover:bg-green-700 text-white py-2 px-6 rounded-lg font-semibold"
          >
            ➡️ {t('quiz.trueOrFalse.next')}
          </button>
        </div>
      )}

      <div className="mt-6 text-center text-gray-400 text-sm">
        {t('quiz.trueOrFalse.score')}: {score} / {questions.length} <br />
        🔥 {t('quiz.trueOrFalse.streak')}: {streak}
      </div>
    </div>
  );
};

export default TrueOrFalse;
